import { h } from '../lib/dom.js';
import { getState, setSilent, fmtNum, fmtReporting, toReporting, totalUSDFor, CONFIG } from '../store.js';
import { iconMarkup } from '../icons/index.js';
import {
  setFromAccount, setMoveField, setSendBalance, onMoveContinue, onMoveBack, onMoveConfirm, onMoveDone,
  openAddRecipient, openEditRecipient, validateMoveAmountBlur, go
} from '../actions.js';

// Same Home-style balance box as Transfer's (src/screens/transfer.js) —
// clicking a row both picks that currency and fills Amount with the full
// balance, replacing the removed "Max" button (see setSendBalance).
function balanceBox(w) {
  const rows = Object.entries(w.balances).map(([ccy, amt]) =>
    h('div', { class: 'balance-row balance-row-clickable', onclick: () => setSendBalance(ccy) }, [
      h('span', {}, `${ccy}  ${fmtNum(amt)}`),
      h('span', { class: 'equiv' }, fmtReporting(toReporting(ccy, amt)))
    ])
  );
  return h('div', { class: 'account-card' }, [
    h('div', { class: 'row-top' }, [
      h('span', { class: 'name' }, w.name),
      h('span', { class: 'total' }, fmtReporting(totalUSDFor(w)))
    ]),
    h('div', { class: 'owner' }, w.owner || '—'),
    h('div', { class: 'balances' }, rows)
  ]);
}

function renderForm(s) {
  const m = s.move;
  const curAccount = s.wallets.find((w) => w.id === m.fromAccountId) || s.wallets[0];
  const fromCcyOptions = Object.keys(curAccount.balances);
  if (!fromCcyOptions.includes(s.reportingCurrency)) fromCcyOptions.push(s.reportingCurrency);
  const availableAmt = m.ccy === s.reportingCurrency ? totalUSDFor(curAccount) : (curAccount.balances[m.ccy] || 0);
  const availableFmt = m.ccy === s.reportingCurrency ? fmtReporting(availableAmt) : fmtNum(availableAmt) + ' ' + m.ccy;
  const sendNetwork = CONFIG.NET[m.ccy] || 'Reporting currency · no network';

  const toOptions = [];
  s.wallets.filter((w) => w.id !== m.fromAccountId).forEach((w) => toOptions.push({ value: 'account:' + w.id, label: w.name + ' (internal)' }));
  s.savedRecipients.forEach((r) => toOptions.push({ value: 'recipient:' + r.id, label: r.name }));
  toOptions.push({ value: '__new__', label: '+ New recipient…' });

  const fromSelect = h('select', { onchange: (e) => setFromAccount(e.target.value) },
    s.wallets.map((w) => h('option', { value: w.id, selected: w.id === m.fromAccountId || undefined }, w.name)));

  const toSelect = h('select', {
    onchange: (e) => {
      const v = e.target.value;
      if (v === '__new__') openAddRecipient();
      else setMoveField('toKey', v);
    }
  }, [
    h('option', { value: '', selected: m.toKey === '' || undefined }, 'Select destination…'),
    ...toOptions.map((o) => h('option', { value: o.value, selected: o.value === m.toKey || undefined }, o.label))
  ]);

  const ccySelect = h('select', { onchange: (e) => setMoveField('ccy', e.target.value) },
    fromCcyOptions.map((c) => h('option', { value: c, selected: c === m.ccy || undefined }, c)));

  const [toKind, toRefId] = m.toKey ? m.toKey.split(':') : [];
  const editRecipientBtn = toKind === 'recipient'
    ? h('button', { class: 'icon-btn', 'aria-label': 'Edit this recipient', onclick: () => openEditRecipient(toRefId) }, [
        h('span', { html: iconMarkup('pencil', { size: 14 }) }), ' Edit'
      ])
    : null;

  return h('div', { class: 'stack-lg' }, [
    h('h1', { class: 'screen-title' }, 'Send'),
    h('div', {}, [h('div', { class: 'card-label' }, 'From account'), fromSelect]),
    balanceBox(curAccount),
    h('div', {}, [
      h('div', { class: 'card-label' }, 'To'),
      h('div', { class: 'field-row' }, [toSelect, editRecipientBtn]),
      h('div', { class: 'field-error' }, m.errors.toKey || '')
    ]),
    h('button', { class: 'btn btn-secondary btn-sm', onclick: () => go('recipients') }, 'Manage recipients'),
    h('div', {}, [
      h('div', { class: 'card-label' }, 'Amount'),
      h('div', { class: 'field-row' }, [
        h('input', {
          placeholder: '0.00', value: m.amount,
          oninput: (e) => setSilent({ move: { ...getState().move, amount: e.target.value, errors: { ...getState().move.errors, amount: null } } }),
          onblur: validateMoveAmountBlur
        }),
        ccySelect
      ]),
      h('div', { class: 'field-help' }, `Available: ${availableFmt} · ${sendNetwork}`),
      h('div', { class: 'field-error' }, m.errors.amount || '')
    ]),
    h('div', {}, [
      h('div', { class: 'card-label' }, 'Memo (optional)'),
      h('input', { placeholder: 'Reference or note', value: m.memo, oninput: (e) => setSilent({ move: { ...getState().move, memo: e.target.value } }) })
    ]),
    h('button', { class: 'btn btn-primary', onclick: onMoveContinue }, 'Continue to review')
  ]);
}

function renderReview(s) {
  const m = s.move;
  const curAccount = s.wallets.find((w) => w.id === m.fromAccountId) || s.wallets[0];
  let reviewTo = '';
  let reviewToAddress = '';
  if (m.toKey) {
    const [kind, refId] = m.toKey.split(':');
    if (kind === 'account') {
      const w = s.wallets.find((w) => w.id === refId);
      reviewTo = w ? w.name : '';
    } else if (kind === 'recipient') {
      const r = s.savedRecipients.find((r) => r.id === refId);
      reviewTo = r ? r.name : '';
      // Full address, never truncated — this is the signing confirmation
      // screen (CLAUDE.md: never hide a swapped middle section).
      reviewToAddress = r ? r.address : '';
    }
  }
  const sendNetwork = CONFIG.NET[m.ccy] || 'Reporting currency · no network';
  const row = (label, value) => h('div', { class: 'review-row' }, [
    h('span', { class: 'k' }, label),
    h('span', { class: 'v' }, value)
  ]);

  return h('div', { class: 'stack-lg' }, [
    h('h1', { class: 'screen-title' }, 'Review transfer'),
    h('div', { class: 'card stack' }, [
      row('From', curAccount.name + ' · ' + m.ccy),
      row('To', reviewTo),
      reviewToAddress ? h('div', { class: 'mono review-address' }, reviewToAddress) : null,
      row('Amount', fmtNum(parseFloat(m.amount || 0)) + ' ' + m.ccy),
      row('Network', sendNetwork),
      row('Est. arrival', 'Instant')
    ]),
    m.errors.submit ? h('div', { class: 'field-error' }, m.errors.submit) : null,
    h('div', { class: 'btn-row' }, [
      h('button', { class: 'btn btn-secondary', disabled: m.sending, onclick: onMoveBack }, 'Back'),
      h('button', {
        class: 'btn btn-primary btn-emphasis',
        disabled: m.sending,
        onclick: m.sending ? null : onMoveConfirm
      }, m.sending ? 'Sending…' : 'Confirm transfer')
    ])
  ]);
}

function renderSuccess(s) {
  const t = s.move.resultTx;
  return h('div', { class: 'success-block' }, [
    h('div', { class: 'success-badge' }, h('div', { class: 'check' })),
    h('div', { class: 'success-title' }, 'Transfer complete'),
    h('div', { class: 'success-sub' }, `${t ? fmtNum(t.amount) + ' ' + t.ccy : ''} sent successfully`),
    h('div', { class: 'ref-card' }, [
      `Ref ${t ? t.id : ''}`, h('br'), `Hash ${t ? t.hash : ''}`
    ]),
    h('button', { class: 'btn btn-primary', onclick: onMoveDone }, 'Done')
  ]);
}

export function renderSend() {
  const s = getState();
  const step = s.move.step;
  const inner = step === 'review' ? renderReview(s) : step === 'success' ? renderSuccess(s) : renderForm(s);
  return h('div', { class: 'screen' }, [inner]);
}
